import { Role } from '@/types/auth'
import { Disruption } from '@/types/disruptions'
import { Log } from '@/types/logs'
import { IUserShift } from '@/types/user'

type CommonAction = 'view' | 'create' | 'update' | 'delete'

type LogAction =
    | 'duplicate'
    | 'share'
    | 'createMileage'
    | 'viewOfficialReport'
    | 'manageOfficialReport'
    | 'viewMechanicalInformation'
    | 'manageMechanicalInformation'
    | 'manageUnfinished'
    | 'lock'

type DisruptionAction = 'viewPDF' | 'share' | 'createBulkCurtailment'

type UserAction = 'switchUser'

type PermissionCheck<Key extends keyof Permissions> = boolean | ((data: Permissions[Key]['dataType']) => boolean)

type RolesWithPermissions = {
    [R in Role]: Partial<{
        [Key in keyof Permissions]: Partial<{
            [Action in Permissions[Key]['action']]: PermissionCheck<Key>
        }>
    }>
}

const Resource = {
    Log: 'log',
    Disruption: 'disruption',
    User: 'user',
} as const

export type Resources = (typeof Resource)[keyof typeof Resource]

export type Permissions = {
    [Resource.Log]: {
        dataType: Log
        action: CommonAction | LogAction
    }
    [Resource.Disruption]: {
        dataType: Disruption
        action: CommonAction | DisruptionAction
    }
    [Resource.User]: {
        dataType: IUserShift
        action: UserAction
    }
}

const ROLES = {
    [Role.Controller]: {
        [Resource.Log]: {
            view: true,
            create: true,
            update: true,
            delete: true,
            duplicate: true,
            share: true,
            createMileage: true,
            manageOfficialReport: true,
            manageMechanicalInformation: true,
        },
        [Resource.Disruption]: {
            view: true,
            share: true,
            viewPDF: true,
            createBulkCurtailment: true,
        },
        [Resource.User]: {
            switchUser: true,
        },
    },
    [Role.MileageClerk]: {
        [Resource.Log]: {
            view: true,
            share: true,
            manageUnfinished: true,
            lock: true,
            createMileage: true,
            duplicate: true,
            manageOfficialReport: true,
            manageMechanicalInformation: true,
        },
        [Resource.Disruption]: {
            view: true,
            share: true,
            viewPDF: true,
        },
    },
    [Role.PerformanceManager]: {
        [Resource.Log]: {
            view: true,
            duplicate: true,
            delete: true,
            createMileage: true,
            share: true,
            manageOfficialReport: true,
            manageMechanicalInformation: true,
        },
        [Resource.Disruption]: {
            view: true,
            create: true,
            update: true,
            delete: true,
            share: true,
            viewPDF: true,
        },
    },
    [Role.Reader]: {
        [Resource.Log]: {
            view: true,
            viewMechanicalInformation: true,
            viewOfficialReport: true,
            share: true,
        },
        [Resource.Disruption]: {
            view: true,
            share: true,
            viewPDF: true,
        },
    },
} as const satisfies RolesWithPermissions

export function hasPermission<Resource extends keyof Permissions>(
    userRole: Role | null,
    resource: Resource,
    action: Permissions[Resource]['action'],
    data?: Permissions[Resource]['dataType']
) {
    if (!userRole) {
        return false
    }

    const permission = (ROLES as RolesWithPermissions)?.[userRole]?.[resource]?.[action]

    if (permission == null || permission === undefined) {
        return false
    }

    if (typeof permission === 'boolean') {
        return permission
    }

    return data != null && permission(data)
}
